import React from 'react';
import { ShieldAlert, Lock, Loader2, XCircle, FileText, CheckCircle, ExternalLink, Ban, RotateCcw, Receipt } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

/**
 * Bloc dédié à la certification FNE (DGI Côte d'Ivoire).
 * Sépare clairement la logique de certification fiscale du paiement.
 *
 * 4 états : not_certified | pending | certified | error
 *
 * Props :
 *  - certification (object | null) — objet `operation.fneCertification`
 *  - fneExemption (object | null) — objet `operation.fneExemption` (Phase 2)
 *  - onCertify, onRetry, onViewPdf, onVerifyQR, onExempt, onReinclude (callbacks)
 *  - certifyDisabled (bool) — désactive le CTA "Certifier maintenant" (ex: NCC manquant)
 *  - disabledReason (string) — tooltip explicatif si désactivé
 *  - canExempt (bool) — affiche le bouton "Exempter" (admin only)
 */
export function FneBillingCard({
  certification,
  fneExemption,
  onCertify,
  onRetry,
  onViewPdf,
  onVerifyQR,
  onExempt,
  onReinclude,
  onCreateCreditNote,
  onViewCreditNote,
  creditNote,
  canRefund = false,
  certifyDisabled = false,
  disabledReason,
  canExempt = false,
}) {
  const status = certification?.status;
  const isCertified = ['certified', 'refunded', 'refunded_partial', 'refunded_total'].includes(status);
  const isRefunded = ['refunded', 'refunded_partial', 'refunded_total'].includes(status);
  const isPending = status === 'pending';
  const isError = status === 'error';
  const isExempt = !!fneExemption?.isExempt;
  const isNotCertified = !certification || (!isCertified && !isPending && !isError);
  const refundMeta = certification?.refund || {};
  const refundReference = refundMeta.reference || creditNote?.certificationReference;
  const refundToken = refundMeta.token || refundMeta.rawResponse?.token || creditNote?.verifyUrl;

  // Format date FR si présente
  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const dt = new Date(iso);
      return dt.toLocaleDateString('fr-FR') + ' ' + dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="fne-billing-card" className="rounded-lg border bg-white">
      <div className="px-3 py-2 border-b bg-slate-50 flex items-center gap-2">
        {isCertified ? (
          <Lock className="w-3.5 h-3.5 text-emerald-700" />
        ) : isError ? (
          <XCircle className="w-3.5 h-3.5 text-rose-600" />
        ) : isPending ? (
          <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" />
        ) : (
          <ShieldAlert className="w-3.5 h-3.5 text-amber-600" />
        )}
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">Facturation FNE</span>
        {isCertified && (
          <Badge className="ml-auto bg-emerald-100 text-emerald-800 text-[10px] border-0">🔒 Certifiée</Badge>
        )}
        {isNotCertified && (
          <Badge variant="outline" className="ml-auto text-[10px] border-amber-400 text-amber-700">
            Non certifiée
          </Badge>
        )}
        {isPending && (
          <Badge variant="outline" className="ml-auto text-[10px] border-blue-400 text-blue-700">En cours</Badge>
        )}
        {isError && (
          <Badge variant="outline" className="ml-auto text-[10px] border-rose-400 text-rose-700">Erreur</Badge>
        )}
      </div>

      <div className="p-3">
        {/* Exemption state (Phase 2) — affiché PRIORITAIREMENT */}
        {isExempt && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 mb-2">
            <div className="flex items-start gap-2">
              <Ban className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-amber-900">Opération exemptée FNE</p>
                <p className="text-[11px] text-amber-800 mt-0.5">
                  Motif : <strong>{fneExemption?.reason || '—'}</strong>
                </p>
                {fneExemption?.reasonDetail && (
                  <p className="text-[11px] text-amber-700 mt-0.5 italic">« {fneExemption.reasonDetail} »</p>
                )}
                <p className="text-[10px] text-amber-600 mt-1">
                  Par {fneExemption?.exemptedByEmail || '—'} le{' '}
                  {fneExemption?.exemptedAt ? new Date(fneExemption.exemptedAt).toLocaleString('fr-FR') : '—'}
                </p>
                {canExempt && onReinclude && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-100 mt-2"
                    onClick={onReinclude}
                    data-testid="fne-reinclude-btn"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Ré-inclure dans la FNE
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {isNotCertified && !isExempt && (
          <div className="space-y-2 text-center">
            <p className="text-xs text-slate-600 leading-relaxed">
              Cette facture n&apos;a pas encore été certifiée par la DGI Côte d&apos;Ivoire.
              <br />
              <span className="text-amber-700 font-medium">Requise pour la conformité fiscale.</span>
            </p>
            <Button
              onClick={onCertify}
              disabled={certifyDisabled}
              className="w-full h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
              data-testid="fne-certify-now-btn"
              title={certifyDisabled ? disabledReason : undefined}
            >
              <ShieldAlert className="w-3.5 h-3.5 mr-1.5" />
              Certifier FNE maintenant
            </Button>
            {certifyDisabled && disabledReason && (
              <p className="text-[10px] text-rose-600 italic">{disabledReason}</p>
            )}
          </div>
        )}

        {isPending && (
          <div className="flex flex-col items-center text-center space-y-2 py-2">
            <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
            <p className="text-xs text-slate-600">Transmission à la DGI en cours...</p>
          </div>
        )}

        {isCertified && (
          <div className="space-y-2.5">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-[11px] space-y-1.5">
              <div className="flex justify-between gap-2">
                <span className="text-slate-500 shrink-0">Réf. DGI</span>
                <span className="font-mono font-semibold text-emerald-800 text-right truncate">
                  {certification?.reference || 'N/A'}
                </span>
              </div>
              {certification?.certifiedAt && (
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500 shrink-0">Certifié le</span>
                  <span className="text-slate-700 text-right">{formatDate(certification.certifiedAt)}</span>
                </div>
              )}
              {isRefunded && (
                <div className="mt-1 pt-1 border-t border-amber-300 text-amber-800 space-y-1">
                  <div className="font-semibold flex items-center gap-1">
                    <XCircle className="w-3 h-3" />
                    Annulée par avoir
                  </div>
                  {refundReference && (
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-500 shrink-0">Réf. avoir DGI</span>
                      <span className="font-mono font-semibold text-rose-700 text-right truncate">
                        {refundReference}
                      </span>
                    </div>
                  )}
                  {creditNote?.credit_note_number && (
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-500 shrink-0">N° avoir</span>
                      <span className="text-slate-700 text-right">{creditNote.credit_note_number}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-1.5">
              {onViewPdf && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 h-7 text-[11px]"
                  onClick={onViewPdf}
                  data-testid="fne-view-pdf-btn"
                >
                  <FileText className="w-3 h-3 mr-1" /> PDF certifié
                </Button>
              )}
              {certification?.token && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 h-7 text-[11px] text-blue-700 border-blue-300 hover:bg-blue-50"
                  onClick={onVerifyQR || (() => window.open(certification.token, '_blank', 'noopener'))}
                  data-testid="fne-verify-qr-btn"
                >
                  <ExternalLink className="w-3 h-3 mr-1" /> Vérifier
                </Button>
              )}
            </div>

            {/* Avoir — généré et vérifié sans quitter l'opération */}
            {isRefunded ? (
              <div className="flex gap-1.5">
                {onViewCreditNote && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 h-7 text-[11px] text-rose-700 border-rose-300 hover:bg-rose-50"
                    onClick={onViewCreditNote}
                    data-testid="fne-view-credit-note-btn"
                  >
                    <FileText className="w-3 h-3 mr-1" /> PDF avoir
                  </Button>
                )}
                {refundToken && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 h-7 text-[11px] text-rose-700 border-rose-300 hover:bg-rose-50"
                    onClick={() => window.open(refundToken, '_blank', 'noopener')}
                    data-testid="fne-verify-credit-note-btn"
                  >
                    <ExternalLink className="w-3 h-3 mr-1" /> Vérifier l&apos;avoir
                  </Button>
                )}
              </div>
            ) : (
              canRefund && onCreateCreditNote && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-7 text-[11px] text-rose-700 border-rose-300 hover:bg-rose-50"
                  onClick={onCreateCreditNote}
                  data-testid="fne-create-credit-note-btn"
                >
                  <Receipt className="w-3 h-3 mr-1" /> Générer un avoir
                </Button>
              )
            )}
          </div>
        )}

        {isError && (
          <div className="space-y-2">
            <div className="bg-rose-50 border border-rose-200 rounded p-2 text-[11px]">
              <p className="text-rose-800 font-semibold flex items-center gap-1">
                <XCircle className="w-3 h-3" /> Échec de certification DGI
              </p>
              <p className="text-rose-700 mt-1 leading-relaxed">
                {certification?.errorMessage || certification?.error || 'Une erreur est survenue lors de la transmission.'}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
              onClick={onRetry || onCertify}
              data-testid="fne-retry-btn"
            >
              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Réessayer la certification
            </Button>
          </div>
        )}

        {/* Bouton "Exempter" — admin only, sauf si déjà exempt ou certifiée */}
        {canExempt && !isExempt && !isCertified && onExempt && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <Button
              variant="outline"
              size="sm"
              className="w-full h-7 text-xs border-amber-200 text-amber-700 hover:bg-amber-50"
              onClick={onExempt}
              data-testid="fne-exempt-btn"
            >
              <Ban className="w-3.5 h-3.5 mr-1" /> Exempter cette opération de la FNE
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default FneBillingCard;
